import { v4 as uuidv4 } from "uuid";

import { PropertyDefinitionOutputTypeEnum } from "src/clients/features-control";
import {
  EntitySchemaProperty,
  EntitySchemaResource,
  PredefinedValuesSchemaProperty,
} from "src/entities/queries";
import { isLikePythonKey } from "src/utils/pythonUtils";

export const isPredefinedValuesProperty = (
  property: EntitySchemaProperty,
): property is PredefinedValuesSchemaProperty => {
  return property._type === "enum" || property._type === "tags";
};

// Newly-added properties are stored under a temporary key while the user types
// their real name. The prefix keeps them distinguishable and collision-free.
const NEW_PROPERTY_KEY_PREFIX = "__new_property_";

export const createNewPropertyKey = (): string =>
  `${NEW_PROPERTY_KEY_PREFIX}${uuidv4()}`;

// Types that can be assigned to a newly-added field. Relations require a target
// schema/cardinality and files are not authorable here, so both are excluded.
export const NEW_PROPERTY_TYPE_OPTIONS = Object.values(
  PropertyDefinitionOutputTypeEnum,
).filter(
  (type) =>
    type !== PropertyDefinitionOutputTypeEnum.RELATION &&
    type !== PropertyDefinitionOutputTypeEnum.FILE,
);

const getMaxPropertyOrder = (
  properties: EntitySchemaResource["properties"] | undefined,
): number =>
  Math.max(
    0,
    ...Object.values(properties ?? {}).map((property) => property._order ?? 0),
  );

export const getNewEntityProperty = (
  properties: EntitySchemaResource["properties"] | undefined,
): EntitySchemaProperty => ({
  _type: PropertyDefinitionOutputTypeEnum.STRING,
  _display_name: "",
  _indexed: false,
  _order: getMaxPropertyOrder(properties) + 1,
  _isNew: true,
  _name: "",
});

export const validateNewPropertyName = (
  name: string,
  properties: EntitySchemaResource["properties"] | undefined,
  currentKey: string,
): string | true => {
  if (!isLikePythonKey(name)) {
    return "Name must start with a letter and contain only letters, numbers, and underscores";
  }

  const takenNames = Object.entries(properties ?? {})
    .filter(([key]) => key !== currentKey)
    .map(([key, property]) => (property._isNew ? (property._name ?? "") : key));

  if (takenNames.includes(name)) {
    return "A field with this name already exists";
  }

  return true;
};

// Rewrites newly-added properties from their temporary key to the user-typed
// name and strips the form-only markers, so the schema is ready to persist.
export const remapNewProperties = (
  schema: EntitySchemaResource,
): EntitySchemaResource => ({
  ...schema,
  properties: Object.fromEntries(
    Object.entries(schema.properties ?? {}).map(([key, property]) => {
      const { _isNew, _name, ...rest } = property;
      return [_isNew && _name ? _name : key, rest];
    }),
  ),
});

export const MASKING_STRATEGIES = [
  {
    key: "full",
    title: "Full masking",
    description:
      "Replace the entire value with a fixed placeholder (e.g., *****). Nothing from the original value is preserved in the output.",
  },
  {
    key: "partial",
    title: "Partial masking",
    description:
      "Show part of the original value (e.g., last 4 digits), while masking the rest with a character.",
  },
  {
    key: "hash",
    title: "Hash masking",
    description:
      "Transforms the entire value into a one-way cryptographic hash (e.g., SHA256). Irreversible and consistent for the same input.",
  },
];

export const FULL_MASKS = ["****", "####", "MASKED", "REDACTED", "custom"];
export const FULL_MASK_LABELS: Record<string, string> = {
  "****": "****",
  "####": "####",
  MASKED: "MASKED",
  REDACTED: "REDACTED",
  custom: "Define custom mask",
};
export const HASH_ALGOS = ["sha256", "sha512"];
